import _ from 'lodash';
import { AftermarketMenuOption, OptionName, ProductConfig, ProductId } from '../types';
import { AftermarketProduct, AftermarketProductVendorApiChannel } from '@trnsact/trnsact-shared-types';

function stripTypeNames<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(v => stripTypeNames(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === '__typename') continue;
      out[k] = stripTypeNames(v as any);
    }
    return out as T;
  }
  return value;
}

export function prepareMenuOptionsForAPI(
  menuOptions: AftermarketMenuOption[],
  financeProgramDescription: string,
  productsConfigInMenuForFirstTerm: Record<OptionName, Record<ProductId, ProductConfig>>
) {
  if (!menuOptions) return null;

  return menuOptions?.map(menuOption => ({
    name: menuOption.name,
    description: menuOption.description,
    titleColor: menuOption.titleColor,
    titleBgColor: menuOption.titleBgColor,
    type: menuOption.type,
    products: menuOption.products.map(mapProduct => {
      const productConfig = productsConfigInMenuForFirstTerm?.[menuOption.name]?.[mapProduct.proposalProductId];

      const product = structuredClone(mapProduct);
      const aftermarketProduct = product.aftermarketProduct ? structuredClone(product.aftermarketProduct) : undefined;

      if (product.markup) {
        delete product.markup.__typename;
        product.markup.markup = _.round(product.markup.markup, 2);
      }

      if (aftermarketProduct) {
        delete (aftermarketProduct as any).sourceOfCreation;
        delete aftermarketProduct.productDescriptionInternal;
        delete aftermarketProduct.createdDateTime;
        delete aftermarketProduct.modifiedDateTime;
        delete aftermarketProduct.productIsNoLongerAvailable;
        delete aftermarketProduct.__typename;

        if (aftermarketProduct.config) {
          delete aftermarketProduct.config.__typename;
          if (productConfig) {
            aftermarketProduct.config.productConfig = productConfig;
          }

          if (productConfig?.aftermarketProduct?.aftermarketProductCatalogDetails) {
            aftermarketProduct.aftermarketProductCatalogDetails = stripTypeNames(
              productConfig?.aftermarketProduct?.aftermarketProductCatalogDetails
            );
          }
        }

        ['aftermarketProductCatalogDetails', 'documents'].forEach(key => {
          const typedKey = key as keyof AftermarketProduct;
          if (aftermarketProduct[typedKey]) {
            aftermarketProduct[typedKey] = stripTypeNames(aftermarketProduct[typedKey]);
          }
        });
      }

      if (aftermarketProduct && aftermarketProduct.partnerLinks) {
        // @ts-expect-error need to get rid of partnerLinks
        delete aftermarketProduct.partnerLinks;
      }

      const isTruckMaster =
        aftermarketProduct?.aftermarketVendorApiChannel === AftermarketProductVendorApiChannel.TruckMasterBuckeye;

      // For TruckMaster: sync criteriaValues[0] with the currently selected plan/term/deductible
      if (isTruckMaster && aftermarketProduct?.config?.criteriaValues?.[0] != null && productConfig) {
        const cv0 = aftermarketProduct.config.criteriaValues[0] as Record<string, unknown>;
        aftermarketProduct.config.criteriaValues = [
          {
            ...cv0,
            subName: productConfig.subName ?? cv0.subName,
            buckeyePlanIndex: productConfig.buckeyePlanIndex ?? cv0.buckeyePlanIndex,
            buckeyeConstructorTermMonths:
              productConfig.buckeyeConstructorTermMonths ?? cv0.buckeyeConstructorTermMonths,
            buckeyeConstructorTermMiles: productConfig.buckeyeConstructorTermMiles ?? cv0.buckeyeConstructorTermMiles,
            buckeyeDeductibleId: productConfig.buckeyeDeductibleId ?? cv0.buckeyeDeductibleId,
            COVERAGE_TERM: productConfig.COVERAGE_TERM ?? cv0.COVERAGE_TERM,
          },
          ...aftermarketProduct.config.criteriaValues.slice(1),
        ];
      }

      const addonsToUse = productConfig?.addons ?? product?.addons ?? [];

      const addonsToSend = addonsToUse.map((addon: any) => {
        const addonToSend = { ...addon };
        delete addonToSend.value;
        return addonToSend;
      });

      // For TruckMaster: include plan name and term in the stored title
      const titleToSend =
        isTruckMaster && productConfig?.subName
          ? `${aftermarketProduct?.productName ?? product.title} ${productConfig.subName}`
          : product.title;

      const aftermarketProductToSend = {
        title: titleToSend,
        addons: addonsToSend,
        cost: Number(productConfig?.cost ?? 0),
        retailCost: Number(productConfig?.retailCost ?? 0),
        thirdPartyId: (product as any).thirdPartyId,
        proposalProductId: product.proposalProductId,
        markup: productConfig
          ? {
              markup: Number(productConfig?.markup?.markup ?? 0),
              type: productConfig?.markup?.type ?? 'FLAT',
            }
          : {
              markup: product?.markup?.markup,
              type: product?.markup?.type,
            },
        aftermarketProductToPartnerLinkId: product.aftermarketProductToPartnerLinkId,
        aftermarketProduct,
      };
      return aftermarketProductToSend;
    }),
    menuOptionDetail: financeProgramDescription,
  }));
}
